import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PERSONA_WORKSPACES } from '@tyndo/types';
import type { UiMenuItem } from '@tyndo/types';
import { Card, CardContent, EmptyState, PageHeader } from '@tyndo/ui-kit';
import { CircleSlash, Compass, SearchX, ShieldAlert } from 'lucide-react';
import {
  actionRegistry,
  menuRegistry,
  moduleManifestRegistry,
  viewRegistry,
  type MenuTreeNode,
} from '../registries';
import { useAuthRoles } from '../auth/useSyncAuthRoles';
import { useModuleAccess } from '../layout/ModuleAccessContext';
import { viewSlugForUrl } from '../utils/urlHelpers';
import {
  resolvePersonaWorkspaceState,
  type WorkSurface,
  type WorkSurfaceGroup,
} from '../layout/personaWorkspace';

/**
 * `/workspace/:persona` — the outcome-first persona workspace.
 *
 * `/browse` is the MODULE-first catalogue: it answers "what does Tyndo ship?".
 * This surface answers the question an operator actually arrives with — "what
 * am I here to do?" — by opening directly on the named work surfaces of the
 * persona's own modules, in the persona manifest's declared priority, and never
 * listing a module the persona doesn't work in.
 *
 * It is composed entirely from what the shell already owns: the persona
 * manifests in `@tyndo/types`, the module/menu/action/view registries the
 * federated modules populate at boot, and the shell's single fail-closed
 * entitlement gate. Every label and every link below is a REAL registered
 * surface — nothing here is authored copy standing in for a capability.
 *
 * Entitlement behaves exactly as everywhere else in the shell: while the gate
 * is `loading` or `unavailable` this page lists nothing at all (no
 * "render-now-filter-later" first paint), and a persona whose modules are all
 * unentitled gets an explicit denied state rather than an empty grid. The
 * persona manifest only ever NARROWS the permitted set — see
 * `layout/personaWorkspace.ts`, which owns every branch.
 */

/**
 * Flatten the role-visible menu tree into deep-linkable work surfaces.
 *
 * A menu leaf earns a surface when its `actionId` resolves to a view the
 * ViewRegistry actually has, which is the same resolution `AppShell`'s menu
 * click performs — so a card here lands exactly where the sidebar entry would,
 * and a menu entry whose view never registered is silently dropped instead of
 * rendering a link to a "view not found" page.
 */
function collectWorkSurfaces(tree: readonly MenuTreeNode[]): WorkSurface[] {
  const viewIds = new Set(
    viewRegistry
      .listViews()
      .map((v) => v.viewId)
      .filter((id): id is string => typeof id === 'string'),
  );
  const surfaces: WorkSurface[] = [];
  const seen = new Set<string>();

  const walk = (nodes: readonly MenuTreeNode[]) => {
    for (const node of nodes) {
      const item: UiMenuItem = node.item;
      const actionId = item.actionId;
      if (actionId && node.moduleId) {
        const viewId = actionRegistry.getAction(actionId)?.viewId;
        if (viewId && viewIds.has(viewId)) {
          const path = `/${node.moduleId}/${viewSlugForUrl(viewId)}`;
          if (!seen.has(path)) {
            seen.add(path);
            surfaces.push({ moduleId: node.moduleId, label: item.label ?? viewId, path });
          }
        }
      }
      walk(node.children);
    }
  };

  walk(tree);
  return surfaces;
}

function SurfaceGroupCard({ group }: { group: WorkSurfaceGroup }) {
  return (
    <Card variant="elevated">
      <CardContent className="p-4">
        <h3 className="text-sm font-semibold text-foreground">{group.moduleName}</h3>
        {group.surfaces.length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">
            No work surfaces registered yet.
          </p>
        ) : (
          <ul className="mt-3 space-y-1">
            {group.surfaces.map((surface) => (
              <li key={surface.path}>
                <Link
                  to={surface.path}
                  className="block rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-surface-raised hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {surface.label}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function SurfaceSection({
  heading, description, groups,
}: {
  heading: string;
  description: string;
  groups: WorkSurfaceGroup[];
}) {
  if (groups.length === 0) return null;
  return (
    <section className="mt-6">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {heading}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {groups.map((group) => (
          <SurfaceGroupCard key={group.moduleId} group={group} />
        ))}
      </div>
    </section>
  );
}

/** Page chrome shared by every state so the frame never flickers between them. */
function WorkspaceFrame({
  title, subtitle, children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-full bg-surface-base">
      <div className="mx-auto max-w-7xl px-6 py-6">
        <PageHeader
          title={title}
          subtitle={subtitle}
          icon={Compass}
          breadcrumbs={[{ label: 'Workspaces', href: '/browse' }, { label: title }]}
        />
        {children}
      </div>
    </div>
  );
}

export default function PersonaWorkspace() {
  const { persona } = useParams<{ persona: string }>();
  const access = useModuleAccess();
  const roles = useAuthRoles();

  // The registries fill in two passes: the gateway manifest's stubs, then each
  // federated bundle's authoritative menus/views. A workspace opened during
  // Pass 1 would otherwise freeze on the stub list forever, so re-derive on
  // every registry mutation — the same subscription ModuleView uses to unblock
  // deep-links rendered before federation completed.
  const [registryRevision, setRegistryRevision] = useState(() =>
    viewRegistry.getRevision(),
  );
  useEffect(
    () => viewRegistry.subscribe(() => setRegistryRevision(viewRegistry.getRevision())),
    [],
  );

  const surfaces = useMemo(
    () => collectWorkSurfaces(menuRegistry.getVisibleMenus(roles)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roles, registryRevision],
  );
  const moduleNames = useMemo(
    () =>
      new Map<string, string>(
        moduleManifestRegistry.list().map((m) => [m.moduleId, m.name ?? m.moduleId]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registryRevision],
  );

  const state = resolvePersonaWorkspaceState({
    persona,
    catalogue: PERSONA_WORKSPACES,
    access,
    surfaces,
    moduleNames,
  });

  if (state.kind === 'not-found') {
    return (
      <WorkspaceFrame
        title="Workspace not found"
        subtitle="No persona workspace is published under this address."
      >
        <Card variant="elevated" className="mt-6 max-w-2xl">
          <CardContent className="p-6">
            <EmptyState
              icon={SearchX}
              title="Workspace not found"
              description={`"${state.persona}" is not a Tyndo persona. Pick a workspace from the persona switcher in the navigation panel, or browse every module you're entitled to open.`}
              data-testid="persona-workspace-not-found"
            />
          </CardContent>
        </Card>
      </WorkspaceFrame>
    );
  }

  const { workspace } = state;

  if (state.kind === 'loading') {
    return (
      <WorkspaceFrame title={workspace.displayName} subtitle="Checking your access…">
        <div
          role="status"
          aria-live="polite"
          data-testid="persona-workspace-loading"
          className="mt-6 flex items-center gap-3 text-sm text-muted-foreground"
        >
          <span className="h-4 w-4 animate-spin motion-reduce:animate-none rounded-full border-b-2 border-primary" />
          <span>Resolving which of this workspace&apos;s surfaces you can open.</span>
        </div>
      </WorkspaceFrame>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <WorkspaceFrame
        title={workspace.displayName}
        subtitle="Access could not be verified."
      >
        <Card variant="elevated" className="mt-6 max-w-2xl">
          <CardContent className="p-6">
            <EmptyState
              icon={ShieldAlert}
              title="The entitlement service didn't answer"
              description="Tyndo can't confirm which of this workspace's surfaces you're allowed to open, so none are listed. Retry from the navigation panel, or contact your administrator if this persists."
              data-testid="persona-workspace-unavailable"
            />
          </CardContent>
        </Card>
      </WorkspaceFrame>
    );
  }

  if (state.kind === 'denied') {
    return (
      <WorkspaceFrame
        title={workspace.displayName}
        subtitle="You aren't entitled to this workspace."
      >
        <Card variant="elevated" className="mt-6 max-w-2xl">
          <CardContent className="p-6">
            <EmptyState
              icon={CircleSlash}
              title="No surfaces in this workspace are available to you"
              description={`The ${workspace.displayName} workspace opens onto modules your account isn't entitled to. Ask your administrator for access, or browse the modules you can open.`}
              data-testid="persona-workspace-denied"
            />
          </CardContent>
        </Card>
      </WorkspaceFrame>
    );
  }

  return (
    <WorkspaceFrame title={workspace.displayName} subtitle={workspace.summary}>
      <div data-testid="persona-workspace" data-persona={workspace.id}>
        <SurfaceSection
          heading="Where your work is"
          description="The surfaces this role runs on, in the order they matter."
          groups={state.primary}
        />
        <SurfaceSection
          heading="Also yours"
          description="Supporting surfaces this role reaches into."
          groups={state.secondary}
        />
      </div>
    </WorkspaceFrame>
  );
}
